import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { LocalCliError } from "./errors.js";

/**
 * Resolve the Key Vault URI from (in order): explicit override, AZURE_KEYVAULT_URI,
 * KEYVAULT_URI. Throws LocalCliError(KV_URI_NOT_FOUND) when nothing resolves.
 *
 * Lifted from the four 4×-duplicated copies in apps/cli/src/commands/brain/*.ts.
 * The (env, override) shape is the canonical one; check-app.ts
 * is normalized to it.
 */
export function discoverKvUri(env: Record<string, string | undefined>, override?: string): string {
  if (override) return override;
  const v = env.AZURE_KEYVAULT_URI ?? env.KEYVAULT_URI;
  if (v) return v;
  throw new LocalCliError({
    code: "KV_URI_NOT_FOUND",
    message: "Could not resolve Key Vault URI.",
    hint: "Pass --kv-uri or set AZURE_KEYVAULT_URI / KEYVAULT_URI.",
  });
}

/**
 * Read the absolute path of the m8t-stack repo checkout from the
 * ~/.m8t-stack/repo-root marker (written by guides/install/m8t.md), or `null`
 * when the marker is absent/empty. The non-throwing sibling of
 * `resolveRepoRoot` — used where the marker's absence is a legitimate fork
 * rather than an error (e.g. the updater job resolves baked content from
 * `/opt/m8t/repo` via this marker, but a founder/dev run has no marker and
 * falls back to fetching release content from the channel).
 */
export function tryResolveRepoRoot(): string | null {
  const marker = path.join(os.homedir(), ".m8t-stack", "repo-root");
  if (!fs.existsSync(marker)) return null;
  const root = fs.readFileSync(marker, "utf8").trim();
  return root.length > 0 ? root : null;
}

/**
 * Read the absolute path of the m8t-stack repo checkout from the
 * ~/.m8t-stack/repo-root marker (written by guides/install/m8t.md). Throws when
 * the marker is missing — that means m8t-stack-POC was never installed.
 */
export function resolveRepoRoot(): string {
  const root = tryResolveRepoRoot();
  if (root === null) {
    throw new LocalCliError({
      code: "M8T_REPO_ROOT_MISSING",
      message: "~/.m8t-stack/repo-root marker missing — m8t install never completed.",
    });
  }
  return root;
}
